import { Alert } from "@components/ui/Alert"
import { AlertDialog } from "@components/ui/AlertDialog"
import { Button } from "@components/ui/Button"
import { AreaSpinner } from "@components/ui/Spinner"
import { alerts } from "@utils/alerts"
import { useRPCMutation, useRPCQuery } from "eddev/hooks"
import { useEffect, useRef } from "react"
import { useAuth } from "../useAuth"
import { AccountDetailsForm, MyInfoForm } from "./AccountDetailsForm"

type Props = {}

function validatePassword(password: string) {
  return password.length >= 8 && /[a-zA-Z]/.test(password) && /[0-9]/.test(password)
}

export function MyInformation(props: Props) {
  const auth = useAuth()
  const ref = useRef<MyInfoForm>(null!)
  const save = useRPCMutation("api.memberships.updateAccountDetails")
  const lookup = useRPCQuery(
    "api.memberships.getAccount",
    { jwt: auth.session?.access_token! },
    {
      staleTime: 1,
      refetchInterval: 0,
    },
  )

  useEffect(() => {
    if (lookup.error) {
      alerts.displayAlert({
        title: "Error",
        description: lookup.error.message,
      })
    }
  }, [lookup.error, !!lookup.data?.details])

  if (lookup.isPending) return null

  return (
    <AlertDialog
      title="Account Details"
      description="See and update your account information below."
      open
      canDismiss
      cancelButton="Cancel"
      onOpenChange={(open) => {
        if (!open) {
          auth.closeModal()
        }
      }}
      confirmButton={
        <Button type="submit" disabled={save.isPending} loading={save.isPending}>
          Save Changes
        </Button>
      }
      onConfirm={async () => {
        const values = ref.current.confirm()
        save.mutate(
          { jwt: auth.session?.access_token!, values: values },
          {
            onError: (error) => {
              alerts.displayAlert({
                title: "Error",
                description:
                  (error as any).data.code === "BAD_REQUEST" ? "Please fill out all required fields." : error.message,
              })
            },
            onSuccess: () => {
              lookup.refetch().then(() => {
                auth.closeModal()
              })
            },
          },
        )
        return false
      }}
    >
      {lookup.isPending ? (
        <AreaSpinner />
      ) : lookup.isError ? (
        <Alert error>{lookup.error.message}</Alert>
      ) : (
        <AccountDetailsForm
          disabled={save.isPending || lookup.isPending}
          account={lookup.data?.details}
          membership={lookup.data?.membership}
          formRef={ref}
        />
      )}
    </AlertDialog>
  )
}
